import React, { useState, useEffect, useCallback, useMemo } from "react";
import { fetchAllProducts } from '../../api/merchandise';
import { createMerchPurchase } from '../../api/merchPurchase';
import { Button, Card, Typography, Modal, List, Divider, Skeleton, Alert, message, Input, Form } from "antd";
import { ShoppingCartOutlined, CreditCardOutlined, CloseOutlined, HomeOutlined } from "@ant-design/icons";
import { useSettings } from "../../contexts/settingsContext";
import { generatePurchasedMerchandisePDF } from "../../utils/merchPurchaseUtils";
import { useNavigate } from 'react-router-dom';
import './frontendMerchandise.scss';

const { Title } = Typography;

// Move validateEmail outside the component to prevent re-creation on each render  
const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(String(email).toLowerCase());
};

const MerchandisePage = () => {
    const [loading, setLoading] = useState(true);
    const [merchandise, setMerchandise] = useState([]);
    const [error, setError] = useState(null);
    const [cart, setCart] = useState([]);
    const [checkoutVisible, setCheckoutVisible] = useState(false);
    const [email, setEmail] = useState('');
    const [emailError, setEmailError] = useState('');
    const { state: settings } = useSettings();
    const navigate = useNavigate();

    // Remove unnecessary dependency  
    const fetchMerchandise = useCallback(async () => {
        try {
            const merchandiseData = await fetchAllProducts();
            setMerchandise(merchandiseData);
        } catch (err) {
            console.error("Error fetching merchandise", err);
            setError("Failed to load merchandise. Please try again later.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchMerchandise();
    }, [fetchMerchandise]);

    // Memoize totalPrice to avoid unnecessary recalculations  
    const totalPrice = useMemo(() =>
        cart.reduce((sum, item) => sum + (item.price || 0), 0),
        [cart],
    );

    // Prevent adding duplicates and memoize function  
    const addToCart = useCallback((item) => {
        if (!cart.some(cartItem => cartItem._id === item._id)) {
            setCart(prevCart => [...prevCart, item]);
            message.success(`${item.description} has been added to your cart.`);
        } else {
            message.info(`${item.description} is already in your cart.`);
        }
    }, [cart]);

    // Compare items by _id and memoize function  
    const removeFromCart = useCallback((item) => {
        setCart(prevCart => prevCart.filter(cartItem => cartItem._id !== item._id));
    }, []);

    const openCheckoutModal = () => {
        setCheckoutVisible(true);
        setEmail('');
    };

    const closeCheckoutModal = () => {
        setCheckoutVisible(false);
    };

    const handlePurchase = async () => {
        if (!validateEmail(email)) {
            setEmailError('Please enter a valid email address.');
            return;
        }

        const merchPurchaseData = cart.map(item => ({
            merch: item._id,
            price: item.price,
            email
        }));

        try {
            // Execute all purchase requests in parallel  
            await Promise.all(merchPurchaseData.map(data => createMerchPurchase(data)));
            generatePurchasedMerchandisePDF(cart);
            setCart([]);
            setEmail('');
            closeCheckoutModal();
            message.success('Your items have been successfully purchased!');
        } catch (error) {
            message.error('Failed to complete purchase. Please try again.');
        }
    };

    const handleEmailChange = (e) => {
        setEmail(e.target.value);
        if (emailError) {
            setEmailError('');
        }
    };

    if (loading) {
        return <Skeleton active />;
    }

    if (error) {
        return <Alert message="Error" description={error} type="error" showIcon />;
    }

    return (
        <div className="merchandise-container">
            <div className="header">
                <Title level={4} className="title">Buy Merchandise</Title>
                <Button type="default" icon={<HomeOutlined />} onClick={() => navigate('/')} className="back-to-login-button">
                    Back to Login
                </Button>
                <Button type="primary" icon={<ShoppingCartOutlined />} onClick={openCheckoutModal} className="checkout-button">
                    Checkout ({cart.length})
                </Button>
            </div>
            <div className="cards-container">
                {merchandise.map(item => (
                    <Card key={item._id} bordered={false} className="main-content">
                        <div className="content">
                            <img src={item.image} alt={item.description} className="product-image" />
                            <Typography.Text className="description">{item.description}</Typography.Text>
                            <Typography.Text className="price">R{item.price}</Typography.Text>
                            <Button type="primary" icon={<ShoppingCartOutlined />} className="add-to-cart" onClick={() => addToCart(item)}>
                                Add to Cart
                            </Button>
                        </div>
                    </Card>
                ))}
            </div>
            <Modal
                visible={checkoutVisible}
                onCancel={closeCheckoutModal}
                footer={null}
            >
                <Title level={4}>Shopping Cart</Title>
                <Divider />
                <List
                    dataSource={cart}
                    renderItem={item => (
                        <List.Item
                            key={item._id}
                            actions={[
                                <Button
                                    type="text"
                                    icon={<CloseOutlined />}
                                    onClick={() => removeFromCart(item)}
                                />
                            ]}
                        >
                            <List.Item.Meta
                                title={item.description}
                                description={`R${item.price}`}
                            />
                        </List.Item>
                    )}
                />
                <Divider />
                <Form layout="vertical">
                    <Form.Item
                        label="Email Address"
                        validateStatus={emailError ? 'error' : ''}
                        help={emailError}
                    >
                        <Input type="email" value={email} onChange={handleEmailChange} />
                    </Form.Item>
                </Form>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingRight: '16px' }}>
                    <Button type="primary" icon={<CreditCardOutlined />} onClick={handlePurchase}>
                        Purchase
                    </Button>
                    <Title level={5} style={{ margin: 0 }}>Total Price: R{totalPrice.toFixed(2)}</Title>
                </div>
            </Modal>
        </div>
    );
};

export default MerchandisePage;  
